import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Html5QrcodeScanner } from 'html5-qrcode';
import axios from 'axios';
import { configService } from '../services/configService';
import { colors } from '../styles/GlobalStyles';

interface BookingInfo {
  bookingNumber: string;
  saldoRimanente: string;
  nome: string;
  cognome: string;
  nomeGruppo: string;
  numeroTavolo: string;
}

const PageWrapper = styled.div`
  max-width: 480px;
  margin: 0 auto;
  padding: 24px 16px 60px;
`;

const PageTitle = styled.h2`
  color: ${colors.brown};
  font-size: 1.5rem;
  margin-bottom: 20px;
  text-align: center;
`;

const Card = styled.div`
  background: ${colors.white};
  border: 1px solid ${colors.secondary};
  border-radius: 2px;
  padding: 20px;
  margin-bottom: 20px;
`;

const SectionLabel = styled.h3`
  color: ${colors.brown};
  font-size: 1rem;
  margin-bottom: 12px;
  letter-spacing: 0.02em;
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  text-align: center;
  color: ${colors.lightBrown};
  font-size: 0.8rem;
  margin: 20px 0;
  text-transform: uppercase;
  letter-spacing: 0.05em;

  &::before, &::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid ${colors.secondary};
  }

  &::before { margin-right: 12px; }
  &::after { margin-left: 12px; }
`;

const SearchRow = styled.div`
  display: flex;
  gap: 8px;
`;

const TextInput = styled.input`
  flex: 1;
  padding: 10px 12px;
  border: 1px solid ${colors.secondary};
  border-radius: 2px;
  font-size: 0.95rem;

  &:focus {
    outline: none;
    border-color: ${colors.primary};
  }
`;

const ActionButton = styled.button<{ $variant?: 'primary' | 'secondary' | 'danger' }>`
  background: ${props =>
    props.$variant === 'danger' ? '#A33A2F' :
    props.$variant === 'secondary' ? colors.white :
    colors.primary};
  color: ${props => props.$variant === 'secondary' ? colors.brown : colors.white};
  border: 1px solid ${props => props.$variant === 'secondary' ? colors.secondary : 'transparent'};
  padding: 12px 20px;
  border-radius: 2px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  width: 100%;
  transition: opacity 0.15s ease;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    opacity: 0.9;
  }
`;

const ResultList = styled.div`
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ResultItem = styled.button`
  text-align: left;
  background: ${colors.cream};
  border: 1px solid ${colors.secondary};
  border-radius: 2px;
  padding: 10px 12px;
  cursor: pointer;
  font-size: 0.9rem;
  color: ${colors.brown};

  &:hover {
    border-color: ${colors.primary};
  }

  b { font-weight: 600; }
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 6px 12px;
  font-size: 0.95rem;
  margin-bottom: 16px;

  dt { color: ${colors.lightBrown}; }
  dd { font-weight: 600; color: ${colors.brown}; }
`;

const BalanceBadge = styled.div<{ $zero: boolean }>`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 2px;
  font-weight: 700;
  background: ${props => props.$zero ? '#E6F0EA' : '#FBEAE6'};
  color: ${props => props.$zero ? '#2E7D4F' : colors.accent};
`;

const BalanceSection = styled.div`
  margin-top: 16px;
  padding: 16px;
  background: ${colors.cream};
  border: 1px solid ${colors.secondary};
  border-radius: 2px;
`;

const ToggleGroup = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
`;

const ToggleOption = styled.button<{ $active: boolean }>`
  flex: 1;
  padding: 14px 10px;
  border-radius: 2px;
  border: 2px solid ${props => props.$active ? colors.primary : colors.secondary};
  background: ${props => props.$active ? colors.primary : colors.white};
  color: ${props => props.$active ? colors.white : colors.brown};
  font-weight: 700;
  font-size: 0.9rem;
  line-height: 1.4;
  cursor: pointer;
  text-align: center;
  transition: all 0.15s ease;

  .sub {
    display: block;
    font-weight: 400;
    font-size: 0.8rem;
    opacity: 0.9;
  }

  &:hover { border-color: ${colors.primary}; }
`;

const AmountInput = styled(TextInput)`
  font-size: 1.3rem;
  font-weight: 700;
  text-align: center;
  margin-bottom: 16px;
`;

const PaymentMethodRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 4px;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(26, 26, 26, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  z-index: 1000;
`;

const ModalBox = styled.div`
  background: ${colors.white};
  border-radius: 2px;
  padding: 28px 24px;
  max-width: 340px;
  width: 100%;
  text-align: center;
`;

const ModalTitle = styled.h3`
  color: ${colors.brown};
  font-size: 1.2rem;
  margin-bottom: 8px;
`;

const ModalAmount = styled.div`
  font-size: 1.8rem;
  font-weight: 700;
  color: ${colors.primary};
  margin-bottom: 20px;
`;

const ModalButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Message = styled.div<{ $success: boolean }>`
  margin-top: 16px;
  padding: 12px;
  border-radius: 2px;
  font-weight: 600;
  background: ${props => props.$success ? '#E6F0EA' : '#FBEAE6'};
  color: ${props => props.$success ? '#2E7D4F' : colors.accent};
`;

const LogItem = styled.li<{ $variant: 'balance' | 'partial' | 'corrected' }>`
  background: ${colors.cream};
  border-radius: 2px;
  border-left: 4px solid ${props =>
    props.$variant === 'balance' ? '#2E7D4F' :
    props.$variant === 'corrected' ? colors.gold :
    colors.primary};
  margin-bottom: 10px;
  padding: 12px;
  font-size: 0.85rem;

  .log-header {
    font-weight: 700;
    display: flex;
    justify-content: space-between;
    color: ${colors.brown};
    margin-bottom: 4px;
  }

  .log-time {
    color: ${colors.lightBrown};
    font-weight: 400;
    font-size: 0.8rem;
  }
`;

const ScanQrPage: React.FC = () => {
  const [extractedBookingNumber, setExtractedBookingNumber] = useState<string>('');
  const [bookingInfo, setBookingInfo] = useState<BookingInfo | null>(null);
  const [error, setError] = useState<string>('');
  const [isConfirming, setIsConfirming] = useState<boolean>(false);
  const [confirmationMessage, setConfirmationMessage] = useState<string>('');
  const [balanceOption, setBalanceOption] = useState<'zero' | 'partial'>('zero');
  const [partialAmount, setPartialAmount] = useState<string>('');
  const [balanceLogs, setBalanceLogs] = useState<any[]>([]);
  const [loadingLogs, setLoadingLogs] = useState<boolean>(false);
  const scannerRef = useRef<Html5QrcodeScanner | null>(null);

  const [searchQuery, setSearchQuery] = useState<string>('');
  const [searchResults, setSearchResults] = useState<BookingInfo[]>([]);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [searchError, setSearchError] = useState<string>('');

  const [showCorrection, setShowCorrection] = useState<boolean>(false);
  const [correctionAmount, setCorrectionAmount] = useState<string>('');
  const [isCorrecting, setIsCorrecting] = useState<boolean>(false);

  const [showCardPopup, setShowCardPopup] = useState<boolean>(false);

  // Carica i log delle ultime conferme pagamento
  const fetchLogs = async () => {
    setLoadingLogs(true);
    try {
      const backendUrl = configService.getBackendUrl();
      const res = await axios.get(`${backendUrl}/api/registration/balance-confirmations-log?limit=10`);
      setBalanceLogs(res.data.logs || []);
    } catch (e) {
      setBalanceLogs([]);
    } finally {
      setLoadingLogs(false);
    }
  };

  // Lo scanner vive finché non c'è una prenotazione aperta (bookingInfo null). Usiamo un
  // effect invece di chiamate imperative perché scanner.clear() di html5-qrcode è
  // asincrono: smontando e rimontando a mano (es. su "Nuova Ricerca") si rischiava di
  // creare il nuovo scanner prima che il vecchio avesse rilasciato la fotocamera, o
  // prima che il div #qr-reader fosse tornato nel DOM dopo l'aggiornamento di stato
  // (per questo la fotocamera non ripartiva subito). L'effect garantisce l'ordine giusto:
  // il cleanup del giro precedente gira sempre prima del nuovo render.
  useEffect(() => {
    if (bookingInfo) return;

    const scanner = new Html5QrcodeScanner(
      'qr-reader',
      {
        fps: 10,
        qrbox: { width: 250, height: 250 },
        aspectRatio: 1.0
      },
      false
    );

    scanner.render(
      (decodedText: string) => {
        handleScan(decodedText);
        scanner.clear().catch(() => {});
      },
      () => {
        // Ignora errori di scansione continua
      }
    );

    scannerRef.current = scanner;

    return () => {
      scanner.clear().catch(() => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingInfo]);

  useEffect(() => {
    fetchLogs();
  }, []);

  const handleScan = async (result: string) => {
    setSearchResults([]);
    try {
      // Il QR code potrebbe contenere un JSON con i dati completi della prenotazione
      // Estraiamo solo il bookingNumber
      let bookingNumber = result;

      try {
        const qrData = JSON.parse(result);
        if (qrData.bookingNumber) {
          bookingNumber = qrData.bookingNumber;
        }
      } catch (parseError) {
        // Se non è JSON, assumiamo che sia direttamente il numero di prenotazione
      }

      setExtractedBookingNumber(bookingNumber);

      const backendUrl = configService.getBackendUrl();
      const res = await axios.get(`${backendUrl}/api/registration/by-booking-number/${encodeURIComponent(bookingNumber)}`);
      setBookingInfo(res.data);
      setError('');
    } catch (err: any) {
      setBookingInfo(null);
      setError(err.response?.data?.error || 'Errore nella ricerca prenotazione');
    }
  };

  const runSearch = async () => {
    const q = searchQuery.trim();
    if (!q) return;

    setIsSearching(true);
    setSearchError('');
    try {
      const backendUrl = configService.getBackendUrl();
      const res = await axios.get(`${backendUrl}/api/registration/search`, { params: { q } });
      setSearchResults(res.data.results || []);
      if (!res.data.results || res.data.results.length === 0) {
        setSearchError('Nessuna prenotazione trovata.');
      }
    } catch (err: any) {
      setSearchResults([]);
      setSearchError(err.response?.data?.error || 'Errore nella ricerca');
    } finally {
      setIsSearching(false);
    }
  };

  const selectResult = (result: BookingInfo) => {
    setBookingInfo(result);
    setExtractedBookingNumber(result.bookingNumber);
    setError('');
    setConfirmationMessage('');
    setBalanceOption('zero');
    setPartialAmount('');
    setShowCorrection(false);
    setCorrectionAmount('');
  };

  const confirmBalance = async () => {
    if (!extractedBookingNumber || !bookingInfo) return;

    // Validazione per importo parziale
    if (balanceOption === 'partial') {
      const amount = parseFloat(partialAmount);
      const currentBalance = parseFloat(bookingInfo.saldoRimanente);

      if (!partialAmount || amount <= 0 || amount > currentBalance) {
        setConfirmationMessage(`❌ Inserisci un importo valido (max €${currentBalance})`);
        return;
      }
    }

    setIsConfirming(true);
    setConfirmationMessage('');

    try {
      const backendUrl = configService.getBackendUrl();

      if (balanceOption === 'zero') {
        // Azzera completamente il saldo
        const res = await axios.post(`${backendUrl}/api/registration/confirm-balance/${encodeURIComponent(extractedBookingNumber)}`);

        if (res.data.success) {
          setConfirmationMessage('✅ Saldo confermato e azzerato con successo!');
          setBookingInfo({ ...bookingInfo, saldoRimanente: '0' });
          fetchLogs();
        }
      } else {
        // Pagamento parziale
        const res = await axios.post(`${backendUrl}/api/registration/partial-payment/${encodeURIComponent(extractedBookingNumber)}`, {
          amount: partialAmount
        });

        if (res.data.success) {
          setConfirmationMessage(`✅ Pagamento parziale di €${res.data.paidAmount} confermato! Nuovo saldo: €${res.data.newBalance}`);
          setBookingInfo({ ...bookingInfo, saldoRimanente: res.data.newBalance });
          fetchLogs();
        }
      }
    } catch (err: any) {
      setConfirmationMessage(`❌ Errore: ${err.response?.data?.error || 'Errore nella conferma del saldo'}`);
    } finally {
      setIsConfirming(false);
    }
  };

  // Il collegamento automatico col POS fisico (deep link SumUp) non funziona (chiave
  // rifiutata da SumUp, meccanismo deprecato). L'operatore fa l'addebito a mano sul
  // POS fisico; qui ci limitiamo a mostrare il popup e aspettare la sua conferma.
  const payWithCard = () => {
    if (!bookingInfo) return;
    setShowCardPopup(true);
  };

  // L'operatore conferma che il POS ha dato esito positivo: registriamo il pagamento
  // esattamente come per i contanti.
  const handleCardPaid = async () => {
    setShowCardPopup(false);
    await confirmBalance();
  };

  // L'operatore dice che il pagamento non è andato a buon fine (o ha annullato): nessuna
  // chiamata al backend, si torna semplicemente alla schermata di pagamento.
  const handleCardBack = () => {
    setShowCardPopup(false);
  };

  const correctLastPayment = async () => {
    if (!extractedBookingNumber || !bookingInfo) return;

    const amount = parseFloat(correctionAmount);
    if (!correctionAmount || amount <= 0) {
      setConfirmationMessage('❌ Inserisci un importo corretto valido');
      return;
    }

    setIsCorrecting(true);
    setConfirmationMessage('');

    try {
      const backendUrl = configService.getBackendUrl();
      const res = await axios.post(`${backendUrl}/api/registration/correct-last-payment/${encodeURIComponent(extractedBookingNumber)}`, {
        newAmount: correctionAmount
      });

      if (res.data.success) {
        setConfirmationMessage(`✅ Importo corretto a €${amount}. Nuovo saldo: €${res.data.newBalance}`);
        setBookingInfo({ ...bookingInfo, saldoRimanente: res.data.newBalance });
        setShowCorrection(false);
        setCorrectionAmount('');
        fetchLogs();
      }
    } catch (err: any) {
      setConfirmationMessage(`❌ Errore: ${err.response?.data?.error || 'Errore nella correzione del pagamento'}`);
    } finally {
      setIsCorrecting(false);
    }
  };

  const startNewScan = () => {
    setExtractedBookingNumber('');
    setBookingInfo(null);
    setError('');
    setConfirmationMessage('');
    setBalanceOption('zero');
    setPartialAmount('');
    setSearchQuery('');
    setSearchResults([]);
    setSearchError('');
    setShowCorrection(false);
    setCorrectionAmount('');
    // Lo scanner riparte da solo: l'effect sopra lo rimonta appena bookingInfo torna null.
  };

  const isPartialInvalid = !partialAmount || parseFloat(partialAmount) <= 0;

  return (
    <PageWrapper>
      <PageTitle>Gestione Ingressi · Aria di Fest</PageTitle>

      {!bookingInfo && (
        <Card>
          <SectionLabel>📷 Scansiona il QR</SectionLabel>
          <div id="qr-reader" style={{ width: '100%' }}></div>

          <Divider>oppure</Divider>

          <SectionLabel>🔎 Cerca manualmente</SectionLabel>
          <SearchRow>
            <TextInput
              placeholder="Nome, cognome, email, gruppo o n° prenotazione"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && runSearch()}
            />
            <ActionButton style={{ width: 'auto' }} onClick={runSearch} disabled={isSearching || !searchQuery.trim()}>
              {isSearching ? '...' : 'Cerca'}
            </ActionButton>
          </SearchRow>

          {searchError && <div style={{ marginTop: 10, color: colors.accent, fontSize: 14 }}>{searchError}</div>}

          {searchResults.length > 0 && (
            <ResultList>
              {searchResults.map((r) => (
                <ResultItem key={r.bookingNumber} onClick={() => selectResult(r)}>
                  <b>{r.nome} {r.cognome}</b> — {r.nomeGruppo || 'senza gruppo'}<br/>
                  <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{r.bookingNumber}</span>
                  {' · '}Saldo: €{r.saldoRimanente}
                  {r.numeroTavolo && ` · Tavolo ${r.numeroTavolo}`}
                </ResultItem>
              ))}
            </ResultList>
          )}
        </Card>
      )}

      {bookingInfo && (
        <Card>
          <SectionLabel>Dettagli prenotazione</SectionLabel>
          <DetailGrid as="dl">
            <dt>Numero</dt><dd style={{ fontFamily: 'monospace' }}>{bookingInfo.bookingNumber}</dd>
            <dt>Nome</dt><dd>{bookingInfo.nome} {bookingInfo.cognome}</dd>
            <dt>Gruppo</dt><dd>{bookingInfo.nomeGruppo || '—'}</dd>
            <dt>Tavolo</dt><dd>{bookingInfo.numeroTavolo || '—'}</dd>
            <dt>Saldo</dt><dd><BalanceBadge $zero={bookingInfo.saldoRimanente === '0'}>€{bookingInfo.saldoRimanente}</BalanceBadge></dd>
          </DetailGrid>

          {bookingInfo.saldoRimanente !== '0' && (
            <BalanceSection>
              <SectionLabel>💰 Gestione Saldo</SectionLabel>

              <ToggleGroup>
                <ToggleOption
                  type="button"
                  $active={balanceOption === 'zero'}
                  onClick={() => setBalanceOption('zero')}
                >
                  ✅ Salda tutto
                  <span className="sub">€{bookingInfo.saldoRimanente}</span>
                </ToggleOption>
                <ToggleOption
                  type="button"
                  $active={balanceOption === 'partial'}
                  onClick={() => setBalanceOption('partial')}
                >
                  💳 Importo parziale
                </ToggleOption>
              </ToggleGroup>

              {balanceOption === 'partial' && (
                <AmountInput
                  type="number"
                  step="0.01"
                  min="0.01"
                  max={parseFloat(bookingInfo.saldoRimanente)}
                  value={partialAmount}
                  onChange={(e) => setPartialAmount(e.target.value)}
                  placeholder={`Max €${bookingInfo.saldoRimanente}`}
                  autoFocus
                />
              )}

              <PaymentMethodRow>
                <ActionButton
                  $variant="secondary"
                  onClick={confirmBalance}
                  disabled={isConfirming || (balanceOption === 'partial' && isPartialInvalid)}
                >
                  {isConfirming ? 'Confermando...' : `💵 Contanti · €${balanceOption === 'zero' ? bookingInfo.saldoRimanente : (partialAmount || '0')}`}
                </ActionButton>
                <ActionButton
                  $variant="secondary"
                  onClick={payWithCard}
                  disabled={isConfirming || (balanceOption === 'partial' && isPartialInvalid)}
                >
                  💳 Paga con carta
                </ActionButton>
              </PaymentMethodRow>
            </BalanceSection>
          )}

          <div style={{ marginTop: 16 }}>
            {!showCorrection ? (
              <button
                onClick={() => setShowCorrection(true)}
                style={{ background: 'none', border: 'none', color: colors.lightBrown, textDecoration: 'underline', fontSize: 13, cursor: 'pointer', padding: 0 }}
              >
                ✏️ Ho sbagliato l'ultimo importo? Correggilo
              </button>
            ) : (
              <BalanceSection>
                <SectionLabel>✏️ Correggi l'ultimo pagamento</SectionLabel>
                <div style={{ fontSize: 13, color: colors.lightBrown, marginBottom: 10 }}>
                  Sostituisce l'importo dell'ultimo movimento registrato per questa prenotazione (es. battuto €50 invece di €45).
                </div>
                <TextInput
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={correctionAmount}
                  onChange={(e) => setCorrectionAmount(e.target.value)}
                  placeholder="Importo corretto"
                  style={{ marginBottom: 10 }}
                />
                <div style={{ display: 'flex', gap: 8 }}>
                  <ActionButton
                    onClick={correctLastPayment}
                    disabled={isCorrecting || !correctionAmount || parseFloat(correctionAmount) <= 0}
                  >
                    {isCorrecting ? 'Correggendo...' : 'Conferma correzione'}
                  </ActionButton>
                  <ActionButton $variant="secondary" onClick={() => { setShowCorrection(false); setCorrectionAmount(''); }}>
                    Annulla
                  </ActionButton>
                </div>
              </BalanceSection>
            )}
          </div>

          <div style={{ marginTop: 16 }}>
            <ActionButton $variant="secondary" onClick={startNewScan}>
              🔄 Nuova Ricerca
            </ActionButton>
          </div>

          {confirmationMessage && (
            <Message $success={confirmationMessage.includes('✅')}>{confirmationMessage}</Message>
          )}
        </Card>
      )}

      {showCardPopup && bookingInfo && (
        <ModalOverlay>
          <ModalBox>
            <ModalTitle>💳 In attesa del POS</ModalTitle>
            <div style={{ color: colors.lightBrown, fontSize: 13, marginBottom: 12 }}>
              Conferma l'esito dopo che il cliente ha pagato sul POS fisico
            </div>
            <ModalAmount>
              €{balanceOption === 'zero' ? bookingInfo.saldoRimanente : (partialAmount || '0')}
            </ModalAmount>
            <ModalButtons>
              <ActionButton onClick={handleCardPaid} disabled={isConfirming}>
                {isConfirming ? 'Confermando...' : '✅ Pagato'}
              </ActionButton>
              <ActionButton $variant="secondary" onClick={handleCardBack} disabled={isConfirming}>
                ⬅️ Indietro
              </ActionButton>
            </ModalButtons>
          </ModalBox>
        </ModalOverlay>
      )}

      {error && (
        <Card>
          <div style={{ color: colors.accent, marginBottom: 12 }}>{error}</div>
          <ActionButton $variant="secondary" onClick={startNewScan}>
            🔄 Nuova Ricerca
          </ActionButton>
        </Card>
      )}

      <Card>
        <SectionLabel>Ultime conferme pagamento</SectionLabel>
        {loadingLogs ? (
          <div style={{ color: colors.lightBrown }}>Caricamento log...</div>
        ) : balanceLogs.length === 0 ? (
          <div style={{ color: colors.lightBrown }}>Nessuna conferma recente.</div>
        ) : (
          <ul style={{ listStyle: 'none', padding: 0 }}>
            {balanceLogs.map((log, idx) => {
              const variant = log.action === 'CONFIRM_BALANCE' ? 'balance' : (log.action === 'CORRECTED_PAYMENT' || log.action === 'ADMIN_CORRECTION') ? 'corrected' : 'partial';
              const label = log.action === 'CONFIRM_BALANCE' ? 'Saldo azzerato' : log.action === 'CORRECTED_PAYMENT' ? 'Pagamento corretto' : log.action === 'ADMIN_CORRECTION' ? 'Corretto da admin' : 'Pagamento parziale';
              return (
                <LogItem key={log.timestamp + log.bookingNumber + idx} $variant={variant}>
                  <div className="log-header">
                    <span>{label}</span>
                    <span className="log-time">{new Date(log.timestamp).toLocaleString()}</span>
                  </div>
                  <div>
                    <b>Prenotazione:</b> {log.bookingNumber}<br/>
                    <b>Gruppo:</b> {log.nomeGruppo || '-'}<br/>
                    <b>Tavolo:</b> {log.numeroTavolo || '-'}<br/>
                    {(log.action === 'CORRECTED_PAYMENT' || log.action === 'ADMIN_CORRECTION') ? (
                      <><b>Importo pagato:</b> da €{log.previousAmount} a €{log.amount}<br/></>
                    ) : (
                      <><b>Importo pagato:</b> {log.amount ? `€${log.amount}` : '-'}<br/></>
                    )}
                    <b>Saldo dopo:</b> {log.saldoRimanente ? `€${log.saldoRimanente}` : '-'}
                  </div>
                </LogItem>
              );
            })}
          </ul>
        )}
      </Card>
    </PageWrapper>
  );
};

export default ScanQrPage;
